import { ConnectProto } from "@racoon/api";
import { Namespace } from "@racoon/i18n";
import { fromTimestamp, getEnumName } from "@racoon/lib";
import type { ExtraFilterItem, ExtraFilterOption } from "~/features/filter/filter.form";

type GovTruckInfo = ConnectProto.GovTruckInfo;

/**
 * Client-side filtering for the govdash trucks registry.
 *
 * The Trucks RPC (`GovOrganizationRequest`) only understands districts and
 * service providers, so every row here runs over the already-loaded
 * `GovTruckInfo[]`. That is fine because the RPC returns the whole registry in
 * one response — there is no pagination to break.
 *
 * Which rows exist is grounded in what the RPC actually carries (verified
 * against the prod DB, 2026-09-11, 248 active trucks in gov geo access):
 *   - equipment type: the deprecated `truck.type` column, well populated — the
 *     live `truck_type.type` is NOT preloaded by GovTrucks (33/493 set anyway)
 *   - make: brand extracted from the free-text `truck.model`, see govTruckBrand
 *   - year: ~55% populated
 *   - garage: 248/248 named depots; shift state / last shift: 206/248 with a
 *     shift, 167 with started_at. These three read `depot` and
 *     `last_driver_on_shift`, which the deployed backend only sends from
 *     go-backend PR #324 (merged 2026-09-03) — on older releases (≤ v1.4.3)
 *     they degrade to "unspecified"/"never" until that ships.
 * Dropped as useless: eco class (9/493) and engine type (0/493) live on
 * `truck_type`, which the RPC never loads; truck status is always ACTIVE (the
 * service hard-filters on it); MVO duplicates the table's own grouping.
 */

/** Option value for trucks where the row's source field is empty. */
const UNSPECIFIED_VALUE = "__none__";

/** Filter row keys applied client-side. */
const TRUCKS_CLIENT_FILTER_KEYS = [
  "equipmentType",
  "brand",
  "year",
  "garage",
  "shiftState",
  "lastShift",
] as const;

/** "Iveco EuroCargo SB-800" — manufacturer plus model, matching the table column. */
export function govTruckModelLabel(info: GovTruckInfo | undefined): string {
  const truck = info?.truck;
  const parts = [truck?.truckType?.manufacturer, truck?.truckType?.model || truck?.model];
  return parts.filter(Boolean).join(" ").trim();
}

// ── Brand extraction ──
//
// `truck.model` is hand-typed: "DAF 75 CF", "Daf CF320", "МАЗ-5907C2-012",
// "MERCEDES-BENZ 3028 zoller", "IZUZI GIGA"… The filter groups them by make so
// one pick catches every spelling. Canonical labels double as option values.

/**
 * First-token (or first-two-token) aliases → canonical brand label. Keys are
 * normalized: uppercased, with `-_./` treated as separators. Covers every brand
 * present in prod plus common misspellings (IZUZI, SHAHMAN, Reno).
 */
const BRAND_ALIASES: Record<string, string> = {
  BMW: "BMW",
  BYD: "BYD",
  COBRA: "Cobra",
  DAF: "DAF",
  DONGFENG: "Dongfeng",
  FAW: "FAW",
  FORD: "Ford",
  FOTON: "Foton",
  HAVAL: "Haval",
  HOWO: "Howo",
  HYUNDAI: "Hyundai",
  ISUZU: "Isuzu",
  ISUZI: "Isuzu",
  IZUZI: "Isuzu",
  IVECO: "Iveco",
  JAC: "JAC",
  KAMAZ: "КАМАЗ",
  MAN: "MAN",
  MAZ: "МАЗ",
  MERCEDES: "Mercedes-Benz",
  MERCEDESBENZ: "Mercedes-Benz",
  PORSCHE: "Porsche",
  RENAULT: "Renault",
  RENO: "Renault",
  SCANIA: "Scania",
  SDLG: "SDLG",
  SHACMAN: "Shacman",
  SHAHMAN: "Shacman",
  SINOTRUK: "Sinotruk",
  TESLA: "Tesla",
  TOYOTA: "Toyota",
  VOLVO: "Volvo",
  ZEEKR: "Zeekr",
  ГАЗ: "ГАЗ",
  ЗИЛ: "ЗИЛ",
  КАМАЗ: "КАМАЗ",
  МАЗ: "МАЗ",
  МТЗ: "МТЗ",
};

/** Placeholder strings that are not brands; they group under "unspecified". */
const NON_BRAND_TOKENS = new Set(["UNKNOWN", "TOBEMARKED", "TEST", "TRUCK", "МУСОРОВОЗ", "ТС"]);

/**
 * Canonical brand for a truck, or null when the model string is empty or
 * carries no recognizable make. Algorithm: normalize (uppercase, separators →
 * spaces), then try the first two tokens joined (catches "DONG FENG"), then the
 * first token, against the alias table; an unknown first token becomes its own
 * capitalized group so new brands never silently vanish into "unspecified".
 */
function govTruckBrand(info: GovTruckInfo): string | null {
  const source = govTruckModelLabel(info);
  if (!source) return null;
  const tokens = source
    .toUpperCase()
    .replace(/[-_./]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return null;

  const joined = tokens.slice(0, 2).join("");
  if (tokens.length >= 2 && BRAND_ALIASES[joined]) return BRAND_ALIASES[joined];
  const first = tokens[0];
  if (BRAND_ALIASES[first]) return BRAND_ALIASES[first];
  if (NON_BRAND_TOKENS.has(first)) return null;
  // Pure model codes ("45143") are not a make.
  if (/^\d+$/.test(first)) return null;
  return first.charAt(0) + first.slice(1).toLowerCase();
}

/** Equipment type: the live truck_type.type when present, else the legacy column. */
function govTruckEquipmentType(info: GovTruckInfo): number | null {
  const type = info.truck?.truckType?.type || info.truck?.type;
  return type ? type : null;
}

// ── Shift rows ──

type TruckShiftState = "onShift" | "offShift" | "never";

/**
 * `lastDriverOnShift` is the current shift or the most recent closed one, so an
 * open shift (started but not completed) means the truck is on line right now.
 */
function govTruckShiftState(info: GovTruckInfo): TruckShiftState {
  const shift = info.lastDriverOnShift;
  if (!shift?.startedAt) return "never";
  return shift.completedAt ? "offShift" : "onShift";
}

type LastShiftBucket = "today" | "week" | "month" | "older";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Buckets are cumulative the way a person reads them — "last 7 days" includes
 * today — except `older`, which is everything beyond 30 days. Trucks that have
 * never been on shift match no bucket (the shift-state row covers them).
 */
function matchesLastShiftBucket(startedAt: Date, bucket: LastShiftBucket, now: Date): boolean {
  switch (bucket) {
    case "today":
      return (
        startedAt.getFullYear() === now.getFullYear() &&
        startedAt.getMonth() === now.getMonth() &&
        startedAt.getDate() === now.getDate()
      );
    case "week":
      return now.getTime() - startedAt.getTime() <= 7 * DAY_MS;
    case "month":
      return now.getTime() - startedAt.getTime() <= 30 * DAY_MS;
    case "older":
      return now.getTime() - startedAt.getTime() > 30 * DAY_MS;
  }
}

type TFn = (namespace: Namespace, key: any) => string;

export type GovTrucksDerivedOptions = {
  equipmentTypeOptions: ExtraFilterOption[];
  brandOptions: ExtraFilterOption[];
  yearOptions: ExtraFilterOption[];
  garageOptions: ExtraFilterOption[];
};

/**
 * Options derived from the loaded trucks list, so the sheet never offers a value
 * with zero matches. Deliberately computed from the raw (server-scoped)
 * response, never from the client-filtered one — options must not shrink as the
 * user picks them, or the page's reconciliation effect would drop selections
 * right after they are applied. Rows whose source field is empty on some trucks
 * get an "unspecified" option so those trucks stay reachable.
 */
export function deriveGovTrucksFilterOptions(
  trucks: GovTruckInfo[],
  t: TFn,
): GovTrucksDerivedOptions {
  const equipmentTypes = new Set<number>();
  const brands = new Set<string>();
  const years = new Set<number>();
  const garagesById = new Map<string, string>();
  let unspecifiedEquipment = false;
  let unspecifiedBrand = false;
  let unspecifiedYear = false;
  let unspecifiedGarage = false;

  for (const info of trucks) {
    const type = govTruckEquipmentType(info);
    if (type != null) equipmentTypes.add(type);
    else unspecifiedEquipment = true;

    const brand = govTruckBrand(info);
    if (brand) brands.add(brand);
    else unspecifiedBrand = true;

    const year = info.truck?.year;
    if (year) years.add(year);
    else unspecifiedYear = true;

    if (info.depot?.id != null) {
      garagesById.set(String(info.depot.id), info.depot.name || String(info.depot.id));
    } else {
      unspecifiedGarage = true;
    }
  }

  const unspecifiedLabel = t(Namespace.Govdash, "filters.options.unspecified");
  const withUnspecified = (
    options: ExtraFilterOption[],
    add: boolean,
    label: string = unspecifiedLabel,
  ): ExtraFilterOption[] =>
    add && options.length > 0 ? [...options, { value: UNSPECIFIED_VALUE, label }] : options;

  const equipmentTypeOptions = Array.from(equipmentTypes)
    .map((value) => {
      const enumKey = getEnumName(ConnectProto.TruckTypeSchema, value);
      return {
        value: String(value),
        label: enumKey ? t(Namespace.Enum, enumKey) : String(value),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const brandOptions = Array.from(brands)
    .sort((a, b) => a.localeCompare(b))
    .map((brand) => ({ value: brand, label: brand }));

  const yearOptions = Array.from(years)
    .sort((a, b) => b - a)
    .map((year) => ({ value: String(year), label: String(year) }));

  const garageOptions = Array.from(garagesById.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return {
    equipmentTypeOptions: withUnspecified(equipmentTypeOptions, unspecifiedEquipment),
    brandOptions: withUnspecified(brandOptions, unspecifiedBrand),
    yearOptions: withUnspecified(yearOptions, unspecifiedYear),
    garageOptions: withUnspecified(
      garageOptions,
      unspecifiedGarage,
      t(Namespace.Govdash, "filters.garage.options.none"),
    ),
  };
}

function matchesRow(info: GovTruckInfo, key: string, selected: Set<string>, now: Date): boolean {
  switch (key) {
    case "equipmentType": {
      const type = govTruckEquipmentType(info);
      return selected.has(type != null ? String(type) : UNSPECIFIED_VALUE);
    }
    case "brand":
      return selected.has(govTruckBrand(info) ?? UNSPECIFIED_VALUE);
    case "year": {
      const year = info.truck?.year;
      return selected.has(year ? String(year) : UNSPECIFIED_VALUE);
    }
    case "garage":
      return selected.has(info.depot?.id != null ? String(info.depot.id) : UNSPECIFIED_VALUE);
    case "shiftState":
      return selected.has(govTruckShiftState(info));
    case "lastShift": {
      const startedAt = fromTimestamp(info.lastDriverOnShift?.startedAt);
      if (!startedAt) return false;
      return Array.from(selected).some((bucket) =>
        matchesLastShiftBucket(startedAt, bucket as LastShiftBucket, now),
      );
    }
    default:
      // Unknown rows never reject a truck.
      return true;
  }
}

/**
 * Applies every client-side trucks filter row: rows AND together, options
 * within a row OR together. Rows with nothing selected are inactive.
 */
export function applyGovTrucksExtraFilters(
  trucks: GovTruckInfo[],
  filters: ExtraFilterItem[],
  now: Date = new Date(),
): GovTruckInfo[] {
  const active = filters
    .filter(
      (row) =>
        row.selectedOptions.length > 0 &&
        (TRUCKS_CLIENT_FILTER_KEYS as readonly string[]).includes(row.key),
    )
    .map((row) => ({ key: row.key, selected: new Set(row.selectedOptions) }));

  if (active.length === 0) return trucks;

  return trucks.filter((info) =>
    active.every(({ key, selected }) => matchesRow(info, key, selected, now)),
  );
}
